import React, { useEffect, useState } from 'react';
import { Card } from '@material-ui/core';
import { useDataProvider, useShowContext } from 'react-admin';
import {
  dayMap,
  deserializePlanning,
  minutesBetween,
  orderDays,
  toHumanDuration,
} from '../../CleanerContract/detail/Form/Metadata/planningUtils';
import { normalizeSilaeId } from '../../../helpers';
import AvailabilityForm from './AvailabilityForm';

const AvailabilitiesSlotsGrid = ({ serialized, totalWorkTime }) => {
  const planning = deserializePlanning(serialized);

  const assignedMinutes = orderDays.reduce((acc, d) => {
    const shifts = Array.isArray(planning?.[d]) ? planning[d] : [];
    const dayMinutes = shifts
      .filter((s) => {
        return s && s.start && s.end;
      })
      .reduce((sum, s) => {
        return sum + minutesBetween(s.start, s.end);
      }, 0);
    return acc + dayMinutes;
  }, 0);

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 120,
    flex: 1,
  };
  const slotStyle = {
    height: 36,
    borderRadius: 6,
    background: '#f3f6f9',
    border: '1px solid #d6dde6',
    padding: '4px 8px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#1f2d3d',
  };
  const headerStyle = {
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 6,
    textAlign: 'center',
  };

  const grid = (
    <div
      style={{
        display: 'grid',
        width: '100%',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 12,
      }}
    >
      {orderDays.map((d) => {
        const shifts = Array.isArray(planning?.[d]) ? planning[d] : [];
        const dayMinutes = shifts
          .filter((s) => {
            return s && s.start && s.end;
          })
          .reduce((sum, s) => {
            return sum + minutesBetween(s.start, s.end);
          }, 0);
        return (
          <div
            key={d}
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <div style={headerStyle}>{dayMap[d]}</div>
            <div style={columnStyle}>
              {shifts.length === 0 && (
                <div
                  style={{
                    fontSize: 12,
                    color: '#98a6b3',
                    textAlign: 'center',
                  }}
                >
                  —
                </div>
              )}
              {shifts
                .filter((s) => {
                  return s && s.start && s.end;
                })
                .slice()
                .sort((a, b) => {
                  return a.start.localeCompare(b.start);
                })
                .map((s, idx) => {
                  const mins = minutesBetween(s.start, s.end);
                  const label = `${toHumanDuration(mins)} · ${s.start}-${
                    s.end
                  }`;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`${d}-${idx}`} style={slotStyle}>
                      <span>{label}</span>
                    </div>
                  );
                })}
            </div>
            <div
              style={{
                marginTop: 8,
                textAlign: 'center',
                fontSize: 12,
                color: '#506174',
              }}
            >
              Total: {toHumanDuration(dayMinutes)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card style={{ padding: '0.75rem 1rem', width: '100%' }}>
      <h3
        style={{
          margin: '0 0 1.2rem  0',
          textAlign: 'center',
          fontWeight: 500,
          fontSize: '1.4rem',
          lineHeight: '1.5rem',
          letterSpacing: '-0.015em',
        }}
      >
        {'Planning de disponibilité — '}
        <span>{toHumanDuration(assignedMinutes)}</span>
      </h3>
      <div style={{ display: 'flex', width: '100%' }}>{grid}</div>
    </Card>
  );
};

// Availability now lives on the PERSON (plan 014): it is read from
// `care.cleaner_profile.availability`, keyed on the silae matricule, instead of
// the per-fiche `metadata.availabilityPlanning`. We read the serialized string
// here for the summary grid; the editable form (AvailabilityForm) self-saves to
// the same resource.
const AvailabilitiesTab = () => {
  const { record } = useShowContext();
  const dataProvider = useDataProvider();
  const silaeId = normalizeSilaeId(record && record.silae_id);
  const sourceCleanerId = record && record.id ? String(record.id) : '';

  const [serialized, setSerialized] = useState('');

  useEffect(() => {
    if (!silaeId) {
      setSerialized('');
      return undefined;
    }
    let cancelled = false;
    dataProvider
      .getList('cleaner_profile', {
        sort: { field: 'silae_id', order: 'ASC' },
        pagination: { page: 1, perPage: 1 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setSerialized(
          data && data.length > 0 ? data[0].availability || '' : '',
        );
      })
      .catch(() => {
        if (!cancelled) setSerialized('');
      });
    return () => {
      cancelled = true;
    };
  }, [silaeId, dataProvider]);

  return (
    <>
      <AvailabilitiesSlotsGrid serialized={serialized} />
      <div style={{ marginTop: 16 }}>
        <AvailabilityForm
          silaeId={silaeId}
          sourceCleanerId={sourceCleanerId}
          onSaved={setSerialized}
        />
      </div>
    </>
  );
};

export default AvailabilitiesTab;
